import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

import { useAudience } from '../audience';
import { formatMoney } from '../money';
import type { TravelBooking } from '../queries';
import { CarBookingPaymentBreakdown } from './CarBookingPaymentBreakdown';

interface Props {
  booking: TravelBooking;
}

/**
 * El registro comercial que quedó congelado con la reserva: qué se cobra y,
 * para el asesor, con qué margen.
 *
 * El desglose (neto, markup, comisión) es del ASESOR: para una audiencia
 * minorista queda solo lo que se cobra — el mismo criterio y la misma decisión
 * (`useAudience`) que en el detalle de la reserva y en las cards.
 */
export function BookingRecord({ booking }: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();

  const money = (amountMinor?: number, currency?: string) => {
    const resolvedCurrency = currency ?? booking.currency;
    return typeof amountMinor === 'number' && resolvedCurrency
      ? formatMoney({ amountMinor, currency: resolvedCurrency })
      : '—';
  };

  const markup =
    typeof booking.markupValue === 'number'
      ? booking.markupType === 'percentage'
        ? `${booking.markupValue}%`
        : money(Math.round(booking.markupValue * 100))
      : booking.pricingModel === 'commissionable'
        ? t('travelServices.grossPrice')
        : '—';

  const entries: [string, string][] = [
    [t('travelServices.finalPrice'), money(booking.finalAmountMinor)],
    ...(isAgent
      ? ([
        [t('travelServices.detail.net'), money(booking.netAmountMinor)],
        [t('travelServices.detail.markup'), markup],
        ...(booking.pricingModel === 'commissionable'
          ? ([
            [
              t('travelServices.detail.commission'),
              money(booking.commissionAmountMinor, booking.commissionCurrency),
            ],
          ] as [string, string][])
          : []),
      ] as [string, string][])
      : []),
  ];

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('travelServices.booking.recordSection')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {entries.map(([label, value]) => `${label} ${value}`).join(' · ')}
      </Typography>
      <CarBookingPaymentBreakdown booking={booking} isAgent={isAgent} />
    </Box>
  );
}
